/**
 * Reading the design tokens from JavaScript.
 *
 * `tokens.css` is the single source of truth for the palette, and everything
 * declarative gets at it through `var(--…)`. Two things cannot: a `<canvas>`,
 * which has no idea what a custom property is and needs a resolved colour
 * string for `fillStyle`, and any code that needs a *variant* of a token — an
 * alpha the stylesheet does not declare.
 *
 * This module is those two things and nothing else. Like `motion.ts` beside it,
 * it is theme logic that components import: no React, no state, nothing that
 * belongs to one screen. Anything that hard-codes `#00f0ff` in a component
 * instead of coming through here is the second source of truth `tokens.css`
 * warns about — the palette is meant to be changeable in one file.
 */

/**
 * Resolves a design token to the value the stylesheet currently holds.
 *
 * The fallback is not defensive padding: **a WebClip reopened on a filtered SIM
 * can render a frame before its stylesheet lands**, and an empty custom property
 * assigned to `fillStyle` is ignored by the canvas, so the frame draws in
 * whatever colour was set last (black, on a fresh context). A wrong-but-close
 * literal for one frame is invisible; a black globe is not.
 */
export function readToken(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * `Color.copy(alpha = …)` for a CSS hex string.
 *
 * Only `#rgb` and `#rrggbb` are understood, which is every colour `tokens.css`
 * declares. Anything else is handed back untouched rather than mangled — the
 * shape would be right and the alpha silently lost, which is a nicer failure
 * than an invalid `fillStyle` that the canvas ignores entirely.
 *
 * Call this once per value that is going to be reused, not once per use: every
 * alpha in this app is a constant, so a caller drawing at 60fps should resolve
 * its strings when its colours are read and index them from there.
 */
export function withAlpha(hex: string, alpha: number): string {
  const full = hex.replace(/^#([\da-f])([\da-f])([\da-f])$/i, '#$1$1$2$2$3$3');
  if (!/^#[\da-f]{6}$/i.test(full)) return hex;
  const packed = Number.parseInt(full.slice(1), 16);
  const red = (packed >> 16) & 0xff;
  const green = (packed >> 8) & 0xff;
  const blue = packed & 0xff;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
